import {
  AccountBookOutlined,
  BankOutlined,
  FundOutlined,
  HourglassOutlined,
  MoneyCollectOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons'
import { CopyOutlined, LinkOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  List,
  message,
  Select,
  Spin,
  Table,
  TableColumnsType,
  Tooltip,
} from 'antd'
import copy from 'copy-to-clipboard'
import React, { useCallback, useEffect, useState } from 'react'
import Countdown from 'react-countdown'
import { useParams } from 'react-router-dom'
import Web3 from 'web3'

import abiStacke from 'abi/abi-stake.json'
import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { WEB3_PROVIDER } from 'constants/index'
import { DEFAULT_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import CountdownCustom from 'pages/staking/countdown'
import { formatNumber } from 'utils/formatted-number'
import { logError } from 'utils/logger'

import './style.scss'

interface DataTypeList {
  key: string
  poolContract: string
  pageSize: number
  pageIndex: number
  address?: string
  contract: string
  txHash: string
  asset?: string
  amount?: string
  status?: number
  date?: string
  type?: any // 1,2,3,4,7
}

export default function PoolDetail() {
  const [form] = Form.useForm()
  const location = useParams()
  const poolContract = location.contract
  const [infoHaveContract, setInfoHaveContract] = useState<{
    balanceOf?: any
    unstakeable?: any
    getEarnedRewardTokens?: any
    userMap?: any
    timeLock?: any
    stakeTime?: any
    withdrawable?: any
  }>()
  const [dataStaking, setDataStaking] = useState<{
    poolName: string
    stakingTimeSecond: number
  } | null>(null)
  const [callContract, setCallContract] = useState<{
    stakingAPY?: any
    totalStakerCount?: any
    tokenTotalStaked?: any
    poolCap?: any
    remainingPool?: any
  }>({})
  const [dataUsersList, setDataUsersList] = useState([])
  const [filter, setFilter] = useState<any>({})
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [total, setTotal] = useState<number>(0)
  const [address, setAddress] = useState('')
  const [type, setType] = useState('')

  const web3 = new Web3(WEB3_PROVIDER)
  const contract = new web3.eth.Contract(abiStacke, poolContract)

  const poolDetail = useCallback(async () => {
    try {
      const stakingAPY = await contract.methods.stakingAPY().call()
      const totalStakerCount = await contract.methods.totalStakerCount().call()
      const tokenTotalStaked = await contract.methods.tokenTotalStaked().call()
      const poolCap = await contract.methods.maxCap().call()
      const remainingPool = Number(poolCap) - Number(tokenTotalStaked)

      setCallContract({
        stakingAPY,
        totalStakerCount,
        tokenTotalStaked,
        poolCap,
        remainingPool,
      })

      return {
        stakingAPY,
        totalStakerCount,
        tokenTotalStaked,
        poolCap,
        remainingPool,
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }, [contract.methods])

  useEffect(() => {
    poolDetail()
  }, [poolDetail])

  useEffect(() => {
    getDataUsersList({
      ...pagination,
      ...filter,
      poolContract,
      address,
      type,
    })
  }, [pagination, filter, poolContract, address, type])

  const rendererCountDown2 = ({ days, hours, minutes, seconds, completed }: any) => {
    // Render a countdown
    if (completed) {
      return <span>Unstakeable Now</span>
    }
    if (days > 1) {
      return (
        <span className="status-success" style={{ fontSize: 16 }}>
          {days} days : {hours} h : {minutes} m : {seconds} s
        </span>
      )
    } else if (days === 1) {
      return (
        <span className="status-success" style={{ fontSize: 16 }}>
          {days} day : {hours} h : {minutes} m : {seconds} s
        </span>
      )
    } else {
      return (
        <span className="status-success" style={{ fontSize: 16 }}>
          {hours} h : {minutes} m : {seconds} s
        </span>
      )
    }
  }

  const getInfoHaveContract = useCallback(async () => {
    try {
      if (!address || address === '') return
      // contractAddress
      const balanceOf = await contract.methods.balanceOf(address).call()
      const getEarnedRewardTokens = await contract.methods.getEarnedRewardTokens(address).call()
      const userMap: any = await contract.methods.userMap(address).call()

      const timeLock = await contract.methods.stakeLockPeriod(address).call()

      const stakeTime = Number(userMap.stakeTimeFee)
      const timeUnstake = Number(userMap.unstakeTime)
      const getUser = Number(userMap.inUnStakeAmount)
      const lockTimeUnstake = await contract.methods.lockTimePeriod().call()
      const unstakeable =
        Number(timeLock) > 0 && Number(balanceOf) > 0.0000000001 && Number(stakeTime) > 0 ? (
          <>
            {typeof stakeTime === 'number' && stakeTime > 0 ? (
              <>
                <Countdown
                  date={Number(stakeTime) * 1000 + Number(timeLock) * 1000 + 5000}
                  renderer={rendererCountDown2}
                />
              </>
            ) : (
              'Unstakeable Now'
            )}
          </>
        ) : null

      const withdrawable =
        timeUnstake > 0 && getUser > 0 && Number(lockTimeUnstake) > 0 ? (
          <>
            {timeUnstake * 1000 + Number(lockTimeUnstake) * 1000 > Date.now() ? (
              <div className="title">Withdrawable in:</div>
            ) : (
              ''
            )}
            <div className="time-down">
              <Countdown
                date={timeUnstake * 1000 + Number(lockTimeUnstake) * 1000}
                renderer={rendererCountDown2}
              />
            </div>
          </>
        ) : (
          '--'
        )

      setInfoHaveContract({
        balanceOf,
        unstakeable,
        getEarnedRewardTokens,
        userMap,
        timeLock,
        stakeTime,
        withdrawable,
      })

      return {
        balanceOf,
        unstakeable,
        getEarnedRewardTokens,
        userMap,
        timeLock,
        stakeTime,
        withdrawable,
      }
    } catch (error) {
      logError(error)
    }
  }, [address, contract.methods])

  const getDataStaking = async (poolContract: any) => {
    try {
      const res = await AppAdmin.getListStakingDetails(poolContract)
      if (res?.data.success === true) {
        setDataStaking(res?.data.data[0])
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }

  const getDataUsersList = async (payload: any) => {
    try {
      const res = await AppAdmin.getUsersListStaking(payload)
      if (res?.data.success === true) {
        setDataUsersList(res?.data.data)
        setTotal(res?.data.totalCount)
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }

  const columns: TableColumnsType<DataTypeList> = [
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://basescan.org/address/${text}`}
          >{`${text.slice(0, 8)}...${text.slice(-8)}`}</a>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
              style={{ marginLeft: '8px' }}
            />
          </Tooltip>
        </div>
      ),
    },

    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Amount (MEY)',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) =>
        status === 1 ? (
          <span className="status-pending">Pending</span>
        ) : status === 2 ? (
          <span className="status-success">Success</span>
        ) : (
          <span className="status-fail">Fail</span>
        ),
    },

    {
      title: 'txHash',
      dataIndex: 'txHash',
      key: 'txHash',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://basescan.org/tx/${text}`}
          >{`${text.slice(0, 8)}...${text.slice(-8)}`}</a>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<LinkOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
              style={{ marginLeft: '8px' }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
  ]

  const dataUsers = dataUsersList.map((item: DataTypeList, i: number) => ({
    key: i.toString(),
    poolContract: item.poolContract,
    pageSize: item.pageSize,
    pageIndex: item.pageIndex,
    address: item.address,
    contract: item.contract,
    txHash: item.txHash,
    asset: item.asset,
    amount: item.amount,
    status: item.status,
    date: item.date,
    type: item.type,
  }))

  useEffect(() => {
    getDataStaking(poolContract)
  }, [poolContract])

  useEffect(() => {
    getInfoHaveContract()
  }, [address, getInfoHaveContract])

  useEffect(() => {
    if (!address || address === '') return
    const intervalToken = setInterval(async () => {
      try {
        const getEarnedRewardTokens = await contract.methods.getEarnedRewardTokens(address).call()

        setInfoHaveContract((prev) => ({
          ...prev,
          getEarnedRewardTokens,
        }))
      } catch (error) {
        logError(error)
      }
    }, 5000)

    return () => clearInterval(intervalToken)
  }, [address, contract.methods, infoHaveContract?.getEarnedRewardTokens])

  const dataPoolDetail = [
    {
      title: 'Number of Stakers',
      icons: (
        <UsergroupAddOutlined
          className="icons-style"
          style={{
            background: 'darkturquoise',
            color: 'aliceblue',
          }}
        />
      ),
      Value: Number(callContract?.totalStakerCount),
    },
    {
      title: 'Total Staked',
      icons: (
        <BankOutlined
          className="icons-style"
          style={{
            background: 'cornflowerblue',
            color: 'aliceblue',
          }}
        />
      ),
      Value:
        formatNumber(web3.utils.fromWei(callContract?.tokenTotalStaked ?? '0', 'ether'), 3) +
        ' ' +
        'MEY',
    },
    {
      title: 'APR',
      icons: (
        <FundOutlined
          className="icons-style"
          style={{
            background: 'darkorange',
            color: 'aliceblue',
          }}
        />
      ),
      Value: Number(callContract?.stakingAPY) / 100 + '%',
    },

    {
      title: 'Pool Cap',
      icons: (
        <AccountBookOutlined
          className="icons-style"
          style={{
            background: 'chartreuse',
            color: 'darkcyan',
          }}
        />
      ),
      Value:
        formatNumber(web3.utils.fromWei(callContract?.poolCap ?? '0', 'ether'), 3) + ' ' + 'MEY',
    },
    {
      title: 'Remaining Pool',
      icons: (
        <MoneyCollectOutlined
          className="icons-style"
          style={{
            background: 'blueviolet',
            color: 'aliceblue',
          }}
        />
      ),
      Value:
        formatNumber(web3.utils.fromWei(callContract?.remainingPool ?? '0', 'ether'), 3) +
        ' ' +
        'MEY',
    },
    {
      title: 'Stake Ends In',
      icons: (
        <HourglassOutlined
          className="icons-style"
          style={{
            background: 'forestgreen',
            color: '#fff',
          }}
        />
      ),
      Value: (
        <CountdownCustom
          stakingTimeSecond={dataStaking?.stakingTimeSecond ?? 0}
          className="status-success"
        />
      ),
    },
  ]

  const dataPoolDetailHaveContract = [
    {
      title: 'Total Staked',
      Value: infoHaveContract ? (
        formatNumber(web3.utils.fromWei(infoHaveContract?.balanceOf ?? '0', 'ether'), 2) +
        ' ' +
        'MEY'
      ) : (
        <Spin />
      ),
    },
    {
      title: 'Expired in',
      Value: infoHaveContract ? infoHaveContract?.unstakeable : <Spin />,
    },
    {
      title: 'UnStaked Amount',
      style: { width: '100%', maxWidth: '100%' },
      Value: infoHaveContract ? (
        <div>
          <div style={{ textAlign: 'start' }}>
            <div>
              {formatNumber(
                web3.utils.fromWei(infoHaveContract?.userMap?.inUnStakeAmount ?? 0, 'ether'),
                6
              ) +
                ' ' +
                'MEY'}
            </div>
            {/* <div>
              Stake Time:{" "}
              <b>
                {new Date(
                  Number(infoHaveContract?.userMap.stakeTime) * 1000
                ).toLocaleString()}
              </b>
            </div>
            <div>
              Un Stake Time:{" "}
              <b>
                {new Date(
                  Number(infoHaveContract?.userMap.unstakeTime) * 1000
                ).toLocaleString()}
              </b>
            </div> */}
          </div>
        </div>
      ) : (
        <Spin />
      ),
    },
    {
      title: 'Withdrawable in',
      Value: infoHaveContract ? infoHaveContract?.withdrawable : <Spin />,
    },
    {
      title: 'Earned Reward Tokens',
      Value: infoHaveContract ? (
        formatNumber(
          web3.utils.fromWei(infoHaveContract?.getEarnedRewardTokens ?? '0', 'ether'),
          6
        ) +
        ' ' +
        'MEY'
      ) : (
        <Spin />
      ),
    },
  ]

  const statusOptions = [
    {
      value: '',
      label: 'All',
    },
    {
      value: 1,
      label: 'Stake',
    },
    {
      value: 2,
      label: 'UnStake',
    },
    {
      value: 3,
      label: 'Stake rewards',
    },
    {
      value: 4,
      label: 'Withdraw rewards',
    },
    // {
    //   value: 5,
    //   label: "VESTING",
    // },
    // {
    //   value: 6,
    //   label: "P2P_DEPOSIT",
    // },
    {
      value: 7,
      label: 'Withdraw',
    },
    // {
    //   value: 8,
    //   label: "P2P_WITHDRAW",
    // },
    // {
    //   value: 9,
    //   label: "TOKENIZE_DEPOSIT",
    // },
    // {
    //   value: 10,
    //   label: "TOKENIZE_WITHDRAW",
    // },
    // {
    //   value: 11,
    //   label: "TOKENIZE_REFUND",
    // },
    // {
    //   value: 12,
    //   label: "REF_CLAIM_REWARD",
    // },
  ]

  const additionalItems = [BREADCRUMBS.POOL_DETAIL]

  return (
    <div className="main-section pool-detail">
      <Breadcrumb additionalItems={additionalItems} />

      <div className="main-title">{dataStaking?.poolName}</div>

      <List
        grid={{ gutter: 16, column: 3 }}
        dataSource={dataPoolDetail}
        className="pool-detail-card"
        renderItem={(item) => (
          <>
            <List.Item>
              {
                <Card className={'card-style'}>
                  <div className={'content-style'}>
                    <div>{item.icons}</div>
                    {Object.keys(callContract).length > 0 ? (
                      <div>
                        <div className={'title-style'}>{item.title}</div>
                        <div className={'value-style'}>{item.Value}</div>
                      </div>
                    ) : (
                      <Spin />
                    )}
                  </div>
                </Card>
              }
            </List.Item>
          </>
        )}
      />

      <Card className="formSearch">
        <Form
          form={form}
          layout="inline"
          onFinish={(values: any) => {
            setAddress(values.address)
            setFilter((prev: any) => ({ ...prev, address: values.address }))
          }}
          style={{ padding: 20, justifyContent: 'start' }}
        >
          <Form.Item
            name="address"
            // rules={[
            //   {
            //     validator(_, value, callback) {
            //       if (!value) {
            //         return Promise.resolve()
            //       }
            //       if (value !== "0x22b0305E2bdD56E49E5e834002191ef72DE94Fa7") {
            //         return Promise.reject("Contract Address is not correct!")
            //       }
            //       return Promise.resolve()
            //     },
            //   },
            // ]}
          >
            <Input
              allowClear
              placeholder="Enter Address"
              style={{ width: '100%' }}
              value={address}
              onChange={(e) => {
                setAddress(e.target.value)
              }}
            />
          </Form.Item>
          <Form.Item name="type">
            <Select
              showSearch
              placeholder="Choose Type"
              optionFilterProp="label"
              filterSort={(optionA, optionB) =>
                String(optionA?.label ?? '')
                  .toLowerCase()
                  .localeCompare(String(optionB?.label ?? '').toLowerCase())
              }
              options={statusOptions}
              value={type}
              onChange={(value) => setType(value)}
              style={{ width: '200px', textAlign: 'start' }}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Search
            </Button>
          </Form.Item>
        </Form>

        {address && (
          <List
            grid={{ gutter: 16, column: 5 }}
            dataSource={dataPoolDetailHaveContract}
            className="pool-detail-table-have-contract"
            renderItem={(item) => (
              <>
                <List.Item>
                  <Descriptions
                    title=" "
                    layout="vertical"
                    size="small"
                    colon={false}
                    style={{ marginBottom: 20, marginTop: 0 }}
                  >
                    <Descriptions.Item label={item.title}>{item.Value}</Descriptions.Item>
                  </Descriptions>
                  {/* <Card title={item.title}>
                    {infoHaveContract &&
                    Object.keys(infoHaveContract).length > 0 ? (
                      <div>{item.Value}</div>
                    ) : (
                      <Spin />
                    )}
                  </Card> */}
                </List.Item>
              </>
            )}
          />
        )}
      </Card>

      <div className="listUsers">
        <div className="main-title">Transaction</div>
        <Card style={{ margin: '0 auto' }}>
          <Table<DataTypeList>
            columns={columns}
            dataSource={dataUsers}
            scroll={{ x: 1000 }}
            bordered
            pagination={
              total <= 10
                ? false
                : {
                    current: pagination.pageIndex + 1,
                    pageSize: pagination.pageSize,
                    pageSizeOptions: DEFAULT_PAGE_OPTIONS,
                    total: total,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    onShowSizeChange(current, pageSize) {
                      setPagination({
                        pageIndex: current - 1,
                        pageSize,
                      })
                    },
                    onChange(page, pageSize) {
                      setPagination({
                        pageIndex: page - 1,
                        pageSize,
                      })
                    },
                  }
            }
          />
        </Card>
      </div>
    </div>
  )
}
